import type { BellPatternId } from "@retreat/core";

/**
 * Customer-facing labels for each signal — shown in the Signals list and the
 * Teaching screen. Plain language: they describe the *moment* and how it sounds,
 * never the instrument's name (no "keisu", "ōgane", "hyoshigi"). A short "· …"
 * hint conveys the character of the sound without jargon.
 */
const BELL_PATTERN_LABELS: Partial<Record<BellPatternId, string>> = {
  // Zen — sitting
  zazen_open:        "The sitting begins · three soft bells",
  zazen_close:       "The sitting ends · two soft bells",
  zazen_deep_open:   "The sitting begins · a deep bell",
  zazen_interval:    "A gentle reminder bell",
  // Zen — retreat framing
  han_opening:       "The retreat begins · wooden board",
  han_closing:       "The retreat ends · wooden board",
  // Zen — walking
  kinhin_begin:      "Walking begins · a deep bell",
  kinhin_interval:   "A small bell while walking",
  kinhin_end:        "Walking ends · wood clack",
  // Zen — chanting
  chant_begin:       "Chanting begins",
  chant_end:         "Chanting ends",
  chant_mokugyo:     "A steady wooden beat",
  // Single bells
  inkin_single:      "A small, bright bell",
  keisu_single:      "A low bell",
  rin_single:        "A gentle bell",
  ogane_single:      "A deep bell",
  oryoki_meal_bell:  "The meal begins",
  mindfulness_bell:  "A gentle bell",
  // Vipassana
  goenka_session_start:    "The sitting begins · a gong",
  goenka_session_end:      "The sitting ends · a gong",
  goenka_adhitthana_bell:  "A sitting of resolve · three gongs",
  vipassana_gong_single:   "A single gong",
  // Tibetan / pan-tradition
  tingsha_pair:       "Bright chimes",
  puja_drilbu:        "A ritual bell",
  singing_bowl_rim:   "A singing bowl",
  damaru_ceremony:    "A hand drum",
  // Chinese / Korean
  morning_108:        "Morning bells",
  yunqing_ceremony:   "A cloud gong",
  zhong_single:       "A temple bell",
  moktak_chant:       "A steady wooden beat",
  four_instruments_begin: "Morning service begins",
  // Ceremony
  ceremony_incense:   "Incense offering",
  ceremony_bow:       "A bell for bowing",
  tingsha_ceremony:   "Clearing chimes",
};

/** Returns the human label for a pattern, falling back to the raw ID. */
export function bellPatternLabel(id: BellPatternId): string {
  return BELL_PATTERN_LABELS[id] ?? id;
}
